/**
 * Settlement rule for a browser-direct upload that may fall back to a server relay.
 *
 * Extracted from `useCFImageUpload` so it can be tested for real. The defect this
 * exists to prevent is an ORDERING one, and ordering is not visible to any test that
 * re-implements the rule: per the XHR spec an `error` at the XHR object is followed
 * by `loadend` at the XHR object IN THE SAME DISPATCH, so a `loadend` handler that
 * settles unconditionally wins the race against an in-flight relay and resolves with
 * the presign id — a key holding no bytes — while also deleting the failure signal
 * the caller used to receive.
 *
 * The hook owns React state; this owns "who settles, and with what".
 *
 * It also owns the relay's EXECUTION half — `postImageUploadRelay`, `relayWithRetry` and
 * `relayImageFallback` below. The DECISION half lives in `~/utils/upload-retry`
 * (`selectRelayFallback`, `RELAY_FALLBACK_MAX_BYTES`). That split is deliberate:
 * a new relay constant belongs on the decide side if something reads it to choose whether
 * to relay, and on this side if it shapes the request that gets sent.
 */

import {
  IMAGE_UPLOAD_RELAY_PRODUCER_HEADER,
  type ClientDeclarableProducer,
} from '~/utils/image-upload-relay-producer';
import type { RelayFallbackFailureReason } from '~/utils/relay-fallback-outcome';

/**
 * The route both upload paths fall back to.
 *
 * Not exported: the only site that BUILDS a relay request is `postImageUploadRelay` below,
 * and handing out the bare path would let a future caller build its own — which is exactly
 * how the producer header goes missing at one of two sites.
 *
 * ⚠ That is not the same as "the only way to reach the relay". From outside this module
 * there are TWO entry points — `postImageUploadRelay` and `relayImageFallback`, which
 * reaches the relay without its caller ever naming the other — and the real multipart
 * caller uses the second. Anything added here that reaches the relay `fetch` is a third,
 * and must set the producer header for the counter's attribution to hold.
 *
 * ⚠ It is one literal for the two CALL SITES, not repo-wide: several test files still spell
 * the path out, deliberately, so that renaming the route (whose real path comes from its
 * filename, `src/pages/api/v1/image-upload/relay.ts`) turns them red rather than following
 * the rename silently. Do not read this as a guarantee that the path exists in one place,
 * and do not put a COUNT here — an earlier version said "three", which was already four by
 * the time it was written and is the kind of number a later reader uses to decide a sweep
 * is complete.
 */
const IMAGE_UPLOAD_RELAY_PATH = '/api/v1/image-upload/relay';
/** The chunked relay, for images over the single-shot cap. Built only by `postChunkedRelayStep`. */
const IMAGE_UPLOAD_RELAY_CHUNKED_PATH = '/api/v1/image-upload/relay-chunked';

/**
 * Build and send the relay POST.
 *
 * 🔴 ONE CONSTRUCTION SITE FOR TWO CALLERS. The single-PUT path (`useCFImageUpload`) and
 * the multipart path (`relayImageFallback`, below) previously each built a near-identical
 * `fetch` — same URL, same method, same Content-Type fallback, same signal. Duplicated,
 * the producer header is one caller away from being forgotten at exactly the site whose
 * traffic nobody can currently see, which is the defect this whole discriminator exists
 * to fix. Consolidating makes "the header is sent" a property of the function rather than
 * a convention both call sites have to remember.
 *
 * `producer` is required, deliberately: a default would let a new caller inherit someone
 * else's label silently, and TypeScript refusing to compile is a better reminder than a
 * comment. The server re-sanitises whatever arrives regardless — see
 * `~/utils/image-upload-relay-producer`.
 *
 * 🔴 Typed to `ClientDeclarableProducer`, the two real upload paths — NOT to the full label
 * union. `unknown` and `other` are the server's own buckets, and `unknown` is the row a
 * rollout is graded on; a caller able to declare it could make a rollout look finished.
 * This used to accept the full union, so that call compiled.
 */
export function postImageUploadRelay(
  file: File,
  opts: { signal: AbortSignal; producer: ClientDeclarableProducer }
): Promise<Response> {
  return fetch(IMAGE_UPLOAD_RELAY_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      [IMAGE_UPLOAD_RELAY_PRODUCER_HEADER]: opts.producer,
    },
    body: file,
    signal: opts.signal,
  });
}

/** The surface of XMLHttpRequest this rule touches — so a test can supply a stub. */
export type SettlementXhr = Pick<XMLHttpRequest, 'addEventListener' | 'readyState' | 'status'>;

export type SettlementOutcome =
  | { kind: 'direct'; success: boolean }
  | { kind: 'relayed'; id: string };

export type SettlementCallbacks = {
  /** Called when the relay produced the key that actually holds the bytes. */
  onRelayed: (id: string) => void;
  onSuccess: () => void;
  onError: () => void;
  onAborted: () => void;
};

/**
 * Wire the terminal XHR events and return a promise that settles exactly once.
 *
 * Resolves with the outcome, or rejects with the error the caller should surface.
 * `relay` is invoked only on a network-layer `error` — never on a non-2xx
 * `loadend`, which means we reached the backend and it rejected us; replaying those
 * bytes through a second route would mask a real fault rather than route around an
 * unreachable host.
 */
export function attachUploadSettlement(
  xhr: SettlementXhr,
  relay: () => Promise<string>,
  callbacks: SettlementCallbacks
): Promise<SettlementOutcome> {
  return new Promise<SettlementOutcome>((resolve, reject) => {
    // Set SYNCHRONOUSLY inside the `error` handler, so the `loadend` that follows in
    // the same dispatch observes it and yields. This flag is the entire fix.
    //
    // ⚠ There is deliberately NO second `settled` latch here. An earlier draft had
    // one; a round-2 audit measured it INERT — `resolve`/`reject` are already
    // idempotent, and the callbacks are gated by `relayPending`, so deleting the
    // latch left every test green. A guard that cannot fail is worse than none: it
    // reads as protection and stops the next person looking for the real one.
    let relayPending = false;

    // 🔴 REACHABLE, unlike the latch described above — do not delete it as another one.
    // `abort()` dispatches `abort` and THEN `loadend`, so once `loadend` reports a
    // non-2xx as an error (below), a user cancel would be overwritten with a failure.
    // `keeps a user cancel reported as aborted, not errored` fails without this.
    //
    // ⚠ ONE HONEST QUALIFICATION, so this guard is not over-trusted: as of this change
    // NOTHING IN PRODUCTION TRIGGERS IT. `useCFImageUpload` exposes `abort` on each
    // tracked file, but no consumer of that hook calls it — checked by enumerating every
    // non-test file that calls `useCFImageUpload` and grepping each for `abort`; the only
    // hit is `IterativeImageEditor`'s `handleAbort`, which cancels a GENERATION, not an
    // upload. The `abort` path is pre-existing and unwired. Keep the guard: it is
    // reachable by this module's own contract, it is what the cancel test exercises, and
    // wiring a cancel button is a UI change away from making it live. But do not read its
    // presence as evidence that cancel is a supported flow today.
    let aborted = false;

    xhr.addEventListener('loadend', () => {
      // A relay is in flight and owns settlement.
      if (relayPending) return;
      // A cancel already settled this; see the flag's comment.
      if (aborted) return;
      // 🔴 2xx, not `=== 200`. A store answering 201/204 stored the object; treating
      // that as failure is the same defect in the other direction.
      const success = xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300;
      // 🔴 A non-2xx `loadend` means we REACHED the backend and it refused us. Before
      // this branch existed no callback fired at all, so the tracked file kept its
      // `uploading` status forever — a refused upload that renders as still in flight,
      // and which permanently disabled any submit gated on that status.
      if (success) callbacks.onSuccess();
      else callbacks.onError();
      resolve({ kind: 'direct', success });
    });

    xhr.addEventListener('error', () => {
      relayPending = true;
      relay()
        .then((relayedId) => {
          callbacks.onRelayed(relayedId);
          callbacks.onSuccess();
          resolve({ kind: 'relayed', id: relayedId });
        })
        .catch((relayError: unknown) => {
          // A cancel during the fallback is a cancel, not an upload failure. No signal to
          // consult here — the relay closure owns it — so this site is shape-only.
          if (isClientAbort(relayError)) {
            callbacks.onAborted();
            reject(new Error('Upload canceled'));
            return;
          }
          // Otherwise report the ORIGINAL failure. The fallback is a recovery
          // attempt; surfacing its error instead would rename the user's problem.
          callbacks.onError();
          reject(new Error(`Upload failed (status ${xhr.status})`));
        });
    });

    xhr.addEventListener('abort', () => {
      aborted = true;
      callbacks.onAborted();
      reject(new Error('Upload canceled'));
    });
  });
}

/** The bits of a response this helper reads. Generic so callers keep `Response`. */
type RetryableResponse = {
  ok: boolean;
  status: number;
  headers: { get(name: string): string | null };
};

/** Upper bound on an honoured `Retry-After`. A fallback upload is interactive. */
export const MAX_RETRY_AFTER_SECONDS = 10;

/**
 * POST to the relay, retrying ONCE if it sheds with 429.
 *
 * Without a retry a shed is TERMINAL: the settlement rule deliberately reports the
 * ORIGINAL upload error rather than the relay's, so a shed file would fail
 * permanently while the server was saying "try again shortly".
 *
 * ⚠ An earlier version of this comment justified the retry by claiming a dropzone
 * batch of 10 against a per-pod cap of 8 routinely sheds two. That was wrong — the
 * cap is per POD, the pool's replica floor is in the dozens, and there is no session
 * affinity, so one browser's concurrent POSTs spread across pods. A shed is an
 * ANOMALY, not routine. The retry is still worth having (it is cheap, and a lost
 * upload is not), but do not read a 429 here as business as usual.
 *
 * Retries ONCE and only on 429. Any other non-ok status is a real failure and is
 * surfaced; retrying it would just double the load that produced it.
 *
 * 🔴 The delay is CLAMPED, and the wait is CANCELLABLE. `Retry-After` is remote input
 * and this retry fires on ANY 429, not only our own relay's shed — a CDN or an edge
 * rate-limiter in front of us can answer 429 with a `Retry-After` of an hour. Without
 * the clamp the hook would sit on the user's `File` for that hour with the tracked
 * file stuck at `uploading`; without the abort race the cancel button would be inert
 * for the whole wait, because the abort check only runs AFTER the sleep resolves.
 */
export async function relayWithRetry<T extends RetryableResponse>(
  post: () => Promise<T>,
  opts: {
    signal: AbortSignal;
    sleep: (ms: number) => Promise<void>;
    defaultRetryAfterSeconds: number;
  }
): Promise<T> {
  let response = await post();
  if (response.status === 429) {
    const advertised = Number(response.headers.get('Retry-After'));
    const requested =
      Number.isFinite(advertised) && advertised > 0 ? advertised : opts.defaultRetryAfterSeconds;
    const seconds = Math.min(requested, MAX_RETRY_AFTER_SECONDS);

    // Race the wait against cancellation, so an abort mid-backoff is observed when it
    // happens rather than whenever the timer would have expired.
    await Promise.race([
      opts.sleep(seconds * 1000),
      new Promise<void>((_, rejectWait) => {
        if (opts.signal.aborted) return rejectWait(abortError());
        opts.signal.addEventListener('abort', () => rejectWait(abortError()), { once: true });
      }),
    ]);

    // Also covers a sleep that resolved normally on an already-aborted signal.
    if (opts.signal.aborted) throw abortError();
    response = await post();
  }
  return response;
}

function abortError() {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * Did the person cancel? Answered from the SIGNAL where one is available, not from the
 * error's shape alone: `abort(reason)` rejects with that reason verbatim, so a cancel
 * carrying a plain `Error` — an ordinary idiom — is indistinguishable by shape from a
 * genuine failure. (`isClientAbortError` in `~/server/utils/errorHandling` is the
 * server-side equivalent and is not importable from client code.)
 */
function isClientAbort(err: unknown, signal?: AbortSignal): boolean {
  if (signal?.aborted) return true;
  return err instanceof DOMException && err.name === 'AbortError';
}

export type RelayFallbackResult =
  | { ok: true; id: string }
  | { ok: false; reason: RelayFallbackFailureReason };

/**
 * Execute the multipart upload's relay fallback: POST the whole file through our own
 * origin (`/api/v1/image-upload/relay`) and resolve the relay-minted key, or the reason
 * the rescue did not produce one.
 *
 * The DECISION to call this lives in `selectRelayFallback` (~/utils/upload-retry);
 * this is the execution half, extracted from `useS3Upload` so it is unit-testable the
 * same way `attachUploadSettlement` is.
 *
 * 🔴 NEVER THROWS, for any failure. The caller falls through to the normal terminal-error
 * path on `ok: false`, so a broken fallback degrades to "the upload failed" (the
 * pre-existing outcome) rather than replacing the user's real diagnosis with a fallback
 * error.
 *
 * The relay mints its OWN key server-side (an overwrite guard it enforces by accepting
 * no caller key), so the returned id is NOT the presigned key the multipart session was
 * opened with — callers must report the bytes under this id, and the orphaned session
 * stays theirs to abort.
 */
export async function relayImageFallback(
  file: File,
  opts: {
    signal: AbortSignal;
    sleep: (ms: number) => Promise<void>;
    defaultRetryAfterSeconds: number;
  }
): Promise<RelayFallbackResult> {
  try {
    const res = await relayWithRetry(
      // `multipart`: this is the execution half of the MULTIPART path's rescue. The
      // label is what lets the usage counter answer "is the multipart fallback working?"
      // — before it existed, every relay success was attributable to the single-PUT
      // caller, which shipped first.
      () => postImageUploadRelay(file, { signal: opts.signal, producer: 'multipart' }),
      opts
    );
    if (!res.ok) return { ok: false, reason: 'non_2xx' };
    let id: unknown;
    try {
      id = ((await res.json()) as { id?: unknown }).id;
    } catch (err) {
      // A cancel after the headers arrived rejects the body read, not the `fetch`, so it
      // lands here rather than in the outer catch — and `bad_body` is the one bucket this
      // result exists to measure, so a cancel must not inflate it.
      if (isClientAbort(err, opts.signal)) return { ok: false, reason: 'aborted' };
      // A 2xx body we cannot read is the SAME mode as one carrying no id — the route stored
      // the bytes and counted a success either way. Merging this into the outer catch would
      // report it as a transport failure, i.e. as a request that never arrived.
      return { ok: false, reason: 'bad_body' };
    }
    return typeof id === 'string' && id ? { ok: true, id } : { ok: false, reason: 'bad_body' };
  } catch (err) {
    return {
      ok: false,
      reason: isClientAbort(err, opts.signal) ? 'aborted' : 'transport_error',
    };
  }
}

export type RelayPiece = { partNumber: number; start: number; end: number };

/**
 * The byte ranges the chunked relay expects, in order: `pieceSize` each, the last one short.
 * `pieceSize` comes from the server's `start` response, never a client constant, because the
 * server length-checks every piece against the plan it derived from the same number.
 */
export function planRelayPieces(fileSize: number, pieceSize: number): RelayPiece[] {
  if (!Number.isInteger(fileSize) || fileSize <= 0) return [];
  if (!Number.isInteger(pieceSize) || pieceSize <= 0) return [];
  const pieces: RelayPiece[] = [];
  for (let start = 0; start < fileSize; start += pieceSize) {
    pieces.push({
      partNumber: pieces.length + 1,
      start,
      end: Math.min(start + pieceSize, fileSize),
    });
  }
  return pieces;
}

type ChunkedRelayStep =
  | { action: 'start'; size: number; contentType: string }
  | { action: 'part'; session: string; part: number; body: Blob }
  | { action: 'complete' | 'abort'; session: string };

/**
 * The one construction site for chunked-relay requests, so the producer header cannot go missing
 * from one step. `multipart` because the multipart path is its only caller.
 */
function postChunkedRelayStep(step: ChunkedRelayStep, signal?: AbortSignal): Promise<Response> {
  const params = new URLSearchParams({ action: step.action });
  const headers: Record<string, string> = { [IMAGE_UPLOAD_RELAY_PRODUCER_HEADER]: 'multipart' };
  let body: Blob | undefined;
  if (step.action === 'start') {
    params.set('size', String(step.size));
    headers['Content-Type'] = step.contentType;
  } else {
    params.set('session', step.session);
    if (step.action === 'part') {
      params.set('part', String(step.part));
      headers['Content-Type'] = 'application/octet-stream';
      body = step.body;
    }
  }
  return fetch(`${IMAGE_UPLOAD_RELAY_CHUNKED_PATH}?${params}`, {
    method: 'POST',
    headers,
    body,
    signal,
  });
}

/**
 * The chunked counterpart of `relayImageFallback`, for images over the single-shot relay cap:
 * open a server-side multipart session, send the pieces one at a time, then complete it. Same
 * result shape and the same NEVER-THROWS contract, so the caller's handling is identical.
 *
 * Sequential on purpose: each piece holds one of the server's per-pod buffering slots, and this
 * is a rescue path, not a fast one.
 *
 * On any failure after `start` the server session is aborted best-effort WITHOUT the signal —
 * a cancel is exactly when the multipart upload must still be closed.
 */
export async function relayImageChunkedFallback(
  file: File,
  opts: {
    signal: AbortSignal;
    sleep: (ms: number) => Promise<void>;
    defaultRetryAfterSeconds: number;
    onProgress?: (bytesSent: number) => void;
  }
): Promise<RelayFallbackResult> {
  let session: string | undefined;
  const fail = (reason: RelayFallbackFailureReason): RelayFallbackResult => {
    if (session) postChunkedRelayStep({ action: 'abort', session }).catch(() => undefined);
    return { ok: false, reason };
  };
  const readJson = async (res: Response): Promise<Record<string, unknown> | 'aborted' | null> => {
    try {
      const body: unknown = await res.json();
      return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    } catch (err) {
      return isClientAbort(err, opts.signal) ? 'aborted' : null;
    }
  };

  try {
    const started = await relayWithRetry(
      () =>
        postChunkedRelayStep(
          {
            action: 'start',
            size: file.size,
            contentType: file.type || 'application/octet-stream',
          },
          opts.signal
        ),
      opts
    );
    if (!started.ok) return fail('non_2xx');
    const plan = await readJson(started);
    if (plan === 'aborted') return fail('aborted');
    if (typeof plan?.session === 'string' && plan.session) session = plan.session;
    const pieces = planRelayPieces(file.size, Number(plan?.pieceSize));
    if (!session || pieces.length === 0 || pieces.length !== plan?.partCount)
      return fail('bad_body');

    for (const piece of pieces) {
      const sessionId = session;
      const res = await relayWithRetry(
        () =>
          postChunkedRelayStep(
            {
              action: 'part',
              session: sessionId,
              part: piece.partNumber,
              body: file.slice(piece.start, piece.end),
            },
            opts.signal
          ),
        opts
      );
      if (!res.ok) return fail('non_2xx');
      opts.onProgress?.(piece.end);
    }

    const completed = await postChunkedRelayStep({ action: 'complete', session }, opts.signal);
    if (!completed.ok) return fail('non_2xx');
    // Complete consumed the session server-side; nothing is left to abort.
    session = undefined;
    const done = await readJson(completed);
    if (done === 'aborted') return fail('aborted');
    const id = done?.id;
    return typeof id === 'string' && id ? { ok: true, id } : fail('bad_body');
  } catch (err) {
    return fail(isClientAbort(err, opts.signal) ? 'aborted' : 'transport_error');
  }
}
